// src/utils/error-logger.ts
import api from '../services/api';

// Интерфейс для ошибок API
export interface APIError {
  message: string;
  code: string;
  details?: Record<string, unknown>;
}

// Интерфейс для формата лога ошибки
interface ErrorLog {
  source: string;
  message: string;
  code: string;
  translateKey?: string;
  timestamp: string;
  resolutionTimestamp?: string;
  stackTrace?: string;
  details?: Record<string, unknown>;
}

// Главный класс для логирования ошибок
export class ErrorLogger {
  private static readonly STORAGE_KEY = 'global_error_logs';
  private static readonly DB_NAME = 'globalErrorLogs';
  private static readonly STORE_NAME = 'errors';
  
  // Метод для добавления ключей переводов к кодам ошибок
  private static getTranslateKey(code: string): string | undefined {
    // Example logic to use the 'code' parameter
    const ERROR_TRANSLATION_KEYS: Record<string, string> = {
      'ERR001': 'error.translation.key.001',
      'ERR002': 'error.translation.key.002',
    };
    return ERROR_TRANSLATION_KEYS[code];
  }
  
  /**
   * Логирование ошибки API
   * @param error Объект ошибки API
   * @param source Источник ошибки (название компонента/модуля)
   * @returns Promise<void>
   */
  public static async logError(error: APIError, source: string): Promise<void> {
    // Добавляем ключ перевода для данного кода ошибки
    const translateKey = this.getTranslateKey(error.code);
    
    const errorLog: ErrorLog = {
      source,
      message: error.message,
      code: error.code,
      translateKey: translateKey,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack,
      details: error.details
    };

    // Сохраняем ошибку локально
    await this.storeErrorLocally(errorLog);

    // Отправляем ошибку на бэкенд используя единый эндпоинт из API
    try {
      await api.post(api.endpoints.logs?.errors || '/api/error-logs', errorLog);
    } catch (e) {
      console.error('Не удалось отправить лог ошибки на сервер:', e);
    }
  }

  /**
   * Отметить ошибку как разрешенную
   * @param errorId ID ошибки (обычно timestamp)
   * @returns Promise<void>
   */
  public static async markResolved(errorId: string): Promise<void> {
    const resolutionTimestamp = new Date().toISOString();
    
    try {
      // Сначала попытка обновить в IndexedDB
      const db = await this.getIndexedDB();
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const getRequest = store.get(errorId);
        
        getRequest.onsuccess = () => {
          const error = getRequest.result as ErrorLog;
          if (error) {
            error.resolutionTimestamp = resolutionTimestamp;
            const putRequest = store.put(error);
            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => reject(putRequest.error);
          } else {
            resolve();
          }
        };
        
        getRequest.onerror = () => reject(getRequest.error);
      });
    } catch (e) {
      // Резервное сохранение в localStorage если IndexedDB не доступен
      const logs = this.getLogsFromLocalStorage();
      const errorIndex = logs.findIndex(log => log.timestamp === errorId);
      if (errorIndex !== -1) {
        logs[errorIndex].resolutionTimestamp = resolutionTimestamp;
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(logs));
      }
    }
  }

  /**
   * Получить все сохраненные ошибки
   * @returns Promise<ErrorLog[]>
   */
  public static async getAllErrors(): Promise<ErrorLog[]> {
    try {
      const db = await this.getIndexedDB();
      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const getAllRequest = store.getAll();
        getAllRequest.onsuccess = () => resolve(getAllRequest.result as ErrorLog[]);
        getAllRequest.onerror = () => reject(getAllRequest.error);
      });
    } catch (e) {
      // Если IndexedDB недоступен, используем localStorage
      return this.getLogsFromLocalStorage();
    }
  }

  /**
   * Сохранить ошибку локально (IndexedDB или localStorage)
   * @param errorLog Объект лога ошибки
   * @returns Promise<void>
   */
  private static async storeErrorLocally(errorLog: ErrorLog): Promise<void> {
    try {
      // Сначала пробуем использовать IndexedDB для постоянного хранения
      const db = await this.getIndexedDB();
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const request = store.add(errorLog);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (e) {
      // Если IndexedDB недоступен, используем localStorage
      const existingLogs = this.getLogsFromLocalStorage();
      existingLogs.push(errorLog);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(existingLogs));
    }
  }

  /**
   * Получить логи ошибок из localStorage
   * @returns ErrorLog[]
   */
  private static getLogsFromLocalStorage(): ErrorLog[] {
    try {
      const logsString = localStorage.getItem(this.STORAGE_KEY);
      return logsString ? JSON.parse(logsString) : [];
    } catch (e) {
      console.error('Ошибка при чтении логов из localStorage:', e);
      return [];
    }
  }

  /**
   * Получить подключение к IndexedDB
   * @returns Promise<IDBDatabase>
   */
  private static async getIndexedDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      try {
        const indexedDB = 
          window.indexedDB ||
          (window as any).mozIndexedDB ||
          (window as any).webkitIndexedDB ||
          (window as any).msIndexedDB;

        if (!indexedDB) {
          throw new Error('IndexedDB не поддерживается в этом браузере');
        }

        const openRequest = indexedDB.open(this.DB_NAME, 1);

        openRequest.onerror = () => reject(openRequest.error);
        openRequest.onsuccess = () => resolve(openRequest.result);

        openRequest.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.STORE_NAME)) {
            db.createObjectStore(this.STORE_NAME, { keyPath: 'timestamp' });
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }
}